import { useEffect, useState } from 'react';
import type { AdminDashboard, AdminPeriod } from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';
import { money } from './Billing';

// Admin ana səhifəsi (KT-72 hissə 4): dövr seçimi, kartlar, «Diqqət tələb edir», sistem vəziyyəti (yalnız əsas admin).
// Kartın rəqəmi keçid verdiyi siyahının eyni süzgəcli cəmidir.

const PERIODS: [AdminPeriod, string][] = [
  ['today', 'Bu gün'],
  ['week', 'Bu həftə'],
  ['month', 'Bu ay'],
  ['last_month', 'Keçən ay'],
];

export type HomeLink = { section: 'outlets' | 'billing' | 'requests' | 'companies'; query?: string };

export function AdminHome({ onOpen, can }: { onOpen: (l: HomeLink) => void; can: (s: HomeLink['section']) => boolean }) {
  const [period, setPeriod] = useState<AdminPeriod>('month');
  const [d, setD] = useState<AdminDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Gec gələn köhnə dövr cavabı yeni dövrün üstünə yazılmasın
    let current = true;
    setD(null);
    setError(null);
    adminApi
      .dashboard(period)
      .then((v) => current && setD(v))
      .catch((err) => current && setError(err instanceof AdminApiError ? err.message : 'Şəbəkə xətası.'));
    return () => {
      current = false;
    };
  }, [period]);

  // Keçid yalnız moderatorun açıq bölməsinə; yoxdursa kart klikolunmazdır. null dəyər — bölmə yoxdur, kart göstərilmir
  const card = (label: string, value: string | number | null, link?: HomeLink, tone?: 'bad') => {
    if (value === null) return null;
    const target = link && can(link.section) ? link : undefined;
    return (
    <button type="button" className={`stat-card ${tone ?? ''}`} onClick={() => target && onOpen(target)} disabled={!target}>
      <span className="muted small">{label}</span>
      <strong>{value}</strong>
    </button>
    );
  };

  return (
    <>
      <section className="card card-wide">
        <div className="toolbar">
          <h2>Ana səhifə</h2>
          <span className="topbar-spacer" />
          <div className="tabs" role="tablist" aria-label="Dövr">
            {PERIODS.map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={period === k} className={period === k ? 'tab active' : 'tab'} onClick={() => setPeriod(k)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="bad" role="alert">{error}</p>}
        {!d && !error && <p className="muted">Yüklənir…</p>}
        {d && (
          <>
            <p className="muted small">
              Dövr: {d.from} — {d.to} (son gün daxil deyil)
            </p>
            <div className="stat-grid">
              {card('Aktiv obyektlər', d.activeOutlets, { section: 'outlets', query: 'status=active' })}
              {card('Sınaqda', d.trialOutlets, { section: 'outlets', query: 'status=trial' })}
              {card('Yeni obyektlər', d.newOutlets)}
              {card('Hesablanan', d.billed === null ? null : money(d.billed), { section: 'billing' })}
              {card('Ödənilən', d.paid === null ? null : money(d.paid), { section: 'billing' })}
              {card('Gecikən borc', d.overdueDebt === null ? null : money(d.overdueDebt), { section: 'outlets', query: 'debt=1' }, d.overdueDebt && d.overdueDebt !== '0.00' ? 'bad' : undefined)}
              {card('Sınağı 7 gündə bitən', d.trialsEnding, { section: 'outlets', query: 'trialEnding=7' })}
            </div>
          </>
        )}
      </section>
      {d && (
        <section className="card card-wide">
          <h2>Diqqət tələb edir</h2>
          <ul className="attention-list">
            {d.pendingRequests !== null && (
              <li>
                <button type="button" className="link-btn dark" disabled={!can('requests')} onClick={() => onOpen({ section: 'requests' })}>
                  Gözləyən modul müraciətləri: {d.pendingRequests}
                </button>
              </li>
            )}
            {d.overdueBills !== null && (
              <li>
                <button type="button" className="link-btn dark" disabled={!can('outlets')} onClick={() => onOpen({ section: 'outlets', query: 'debt=1' })}>
                  Gecikən hesabı olan obyektlər: {d.overdueBills}
                </button>
              </li>
            )}
            {d.pendingClaims !== null && (
              <li>
                <button type="button" className="link-btn dark" disabled={!can('billing')} onClick={() => onOpen({ section: 'billing' })}>
                  Yoxlanılmamış ödəniş bəyanları: {d.pendingClaims}
                </button>
              </li>
            )}
          </ul>
          {d.system && (
            <>
              <h3>Sistem vəziyyəti</h3>
              <ul className="attention-list small">
                <li>API versiyası: <code>{d.system.version}</code></li>
                <li>AI açarı: {d.system.aiKey ? 'var' : 'yoxdur'}</li>
              </ul>
            </>
          )}
        </section>
      )}
    </>
  );
}
